import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import AthleteFields from './AthleteFields';
import GuardianDeclaration from './GuardianDeclaration';
import { athleteIsComplete, emptyAthlete, type AthleteDraft } from './athlete-draft';
import { guardianshipsService, type HeldWard } from '../../services/guardianships';
import { isAvatarColor } from '../../lib/avatar-colors';

interface AthleteFormProps {
  /** The child being corrected; left out, the form adds a new one. */
  ward?: HeldWard | null;
  /** The saved child, as the server now holds them. */
  onSaved: (ward: HeldWard) => void;
  onCancel?: () => void;
  /** What the cancel button says, when "back" reads better than "cancel". */
  cancelLabel?: string;
  /** Distinct per form: a page can hold more than one. */
  idPrefix?: string;
}

function draftOf(ward?: HeldWard | null): AthleteDraft {
  if (!ward) return emptyAthlete();
  return {
    key: ward.id,
    name: ward.name ?? '',
    surname: ward.surname ?? '',
    birth_date: ward.birth_date,
    avatar_color: isAvatarColor(ward.avatar_color) ? ward.avatar_color : null,
    gender: ward.gender === 'male' || ward.gender === 'female' ? ward.gender : null,
  };
}

/**
 * Adding a child to this account, or correcting one — the same form wherever a
 * parent does it: on the entry picker at a stage, and on the page where they
 * manage their athletes.
 *
 * The one difference between the two jobs is the consent. Opening an account
 * is the art. 8 declaration and nothing is sent without it; editing a name or
 * a colour on an account that already exists is not a new consent, so it is
 * not asked for again (the version already recorded stands — see
 * Guardianship).
 */
export default function AthleteForm({
  ward = null,
  onSaved,
  onCancel,
  cancelLabel,
  idPrefix = 'athlete',
}: AthleteFormProps) {
  const { t, i18n } = useTranslation();
  const editing = !!ward;
  const [draft, setDraft] = useState<AthleteDraft>(() => draftOf(ward));
  const [declared, setDeclared] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = athleteIsComplete(draft) && (editing || declared) && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const saved = ward
        ? await guardianshipsService.updateWard(ward.id, {
            name: draft.name.trim(),
            surname: draft.surname.trim(),
            birth_date: draft.birth_date,
            avatar_color: draft.avatar_color ?? undefined,
            gender: draft.gender ?? 'not_specified',
          })
        : await guardianshipsService.createWard(
            {
              name: draft.name.trim(),
              surname: draft.surname.trim(),
              birth_date: draft.birth_date,
              avatar_color: draft.avatar_color,
            },
            i18n.language
          );

      onSaved(saved);
      if (!editing) {
        setDraft(emptyAthlete());
        setDeclared(false);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('errors.unexpectedError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="athlete-form">
      {/* The gender only when correcting a child already held: adding one
          stays the three things the declaration is about, and the entry
          dialog asks the gender when it is missing. */}
      <AthleteFields value={draft} onChange={setDraft} idPrefix={idPrefix} withGender={editing} />

      {!editing && (
        <GuardianDeclaration
          id={`${idPrefix}-declaration`}
          checked={declared}
          onCheckedChange={setDeclared}
          name={draft.name.trim() || null}
        />
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex gap-2">
        {onCancel && (
          <Button type="button" variant="outline" className="flex-1" onClick={onCancel} disabled={saving}>
            {cancelLabel ?? t('common.cancel')}
          </Button>
        )}
        <Button
          type="button"
          variant={editing ? 'default' : 'secondary'}
          className="flex-1"
          disabled={!canSave}
          onClick={save}
          data-testid="save-athlete"
        >
          {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
          {editing ? t('common.save') : t('guardian.picker.addConfirm')}
        </Button>
      </div>
    </div>
  );
}
